import { useCallback, useEffect, useState } from "react";
import { Box, Button, Checkbox, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";

export default function AdminClubs() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const result = await adminPost("CLUB_LIST");
    if (result.status === "200") setClubs(result.data?.clubs || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (clubId) => {
    setSelected(clubId);
    const result = await adminPost("CLUB_DETAIL", { club_id: clubId });
    if (result.status === "200") setDetail(result.data);
  };

  const act = async (requestId, status) => {
    const result = await adminPost("CLUB_REQUEST_ACTION", { request_id: requestId, status });
    if (result.status === "200") {
      toast.success(result.data?.message);
      openDetail(selected);
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not update the request");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>Create a joining code for a club. A store that enters it is added to the club when the store is approved.</Typography>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/console/clubs/new")}><Add sx={{ mr: 0.5 }} /> Create club</Button>
      </Box>

      {clubs.length === 0 && <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>No clubs yet.</Box>}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {clubs.map((club) => (
          <Box key={club.club_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{club.title}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>Joining code {club.code} · login {club.username || "not set"} · wallet ₹{Number(club.wallet_amount || 0).toFixed(2)} · {club.store_count} stores · {club.draw_count} draws · {club.pending_count} requests waiting</Typography>
            </Box>
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/clubs/${club.club_id}/edit`)}>Edit</Button>
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => openDetail(club.club_id)}>Open</Button>
          </Box>
        ))}
      </Box>

      {detail?.club && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mt: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{detail.club.title}</Typography>
          <Typography sx={{ fontSize: 13, color: consoleColors.muted, mb: 1.5 }}>Joining code {detail.club.code} · login {detail.club.username || "not set"} · wallet ₹{Number(detail.club.wallet_amount || 0).toFixed(2)}</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.6 }}>Stores</Typography>
          {(detail.club.stores || []).length === 0 && <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>No stores selected yet.</Typography>}
          {(detail.club.stores || []).map((store) => (
            <Typography key={store.store_id} sx={{ fontSize: 14 }}>{store.store_name}{store.city_name ? ` · ${store.city_name}` : ""}</Typography>
          ))}

          <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 2, mb: 0.6 }}>Join requests</Typography>
          {(detail.requests || []).length === 0 && <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>No requests.</Typography>}
          {(detail.requests || []).map((item) => (
            <Box key={item.request_id} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", py: 0.6 }}>
              <Typography sx={{ fontSize: 14, flex: 1 }}>{item.store_name} · {item.status} · {indiaDate(item.date_added)}</Typography>
              {item.status === "pending" && (
                <>
                  <Button size="small" sx={{ textTransform: "none" }} onClick={() => act(item.request_id, "approved")}>Approve</Button>
                  <Button size="small" sx={{ textTransform: "none" }} onClick={() => act(item.request_id, "rejected")}>Reject</Button>
                </>
              )}
            </Box>
          ))}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Lucky Draw Coupons</Typography>
            <Button size="small" sx={primaryButtonSx} onClick={() => navigate(`/console/luckydraw/new?club_id=${detail.club.club_id}`)}>Create Lucky Draw Coupons</Button>
          </Box>
          {(detail.draws || []).map((draw) => (
            <Box key={draw.draw_id} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5 }}>
              <Typography sx={{ fontSize: 14, flex: 1 }}>{draw.title} · {draw.status === "1" ? "Enabled" : "Disabled"}</Typography>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/luckydraw/${draw.draw_id}?view=result`)}>Result</Button>
            </Box>
          ))}
        </Box>
      )}

    </Box>
  );
}

export function ClubForm() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({ club_id: clubId || "", title: "", code: "", username: "", password: "", wallet_amount: "0", club_can_edit_loyalty: "1", store_can_edit_loyalty: "1", store_ids: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const options = await adminPost("CLUB_STORE_OPTIONS");
      if (!mounted) return;
      if (options.status === "200") setStores(options.data?.stores || []);
      if (!clubId) return;
      const full = await adminPost("CLUB_DETAIL", { club_id: clubId });
      if (!mounted || full.status !== "200") return;
      const current = full.data?.club || {};
      setForm({ club_id: current.club_id || clubId, title: current.title || "", code: current.code || "", username: current.username || "", password: "", wallet_amount: String(current.wallet_amount ?? 0), club_can_edit_loyalty: current.club_can_edit_loyalty || "1", store_can_edit_loyalty: current.store_can_edit_loyalty || "1", store_ids: current.store_ids || [] });
    })();
    return () => { mounted = false; };
  }, [clubId]);

  const toggleStore = (storeId) => {
    setForm((prev) => ({
      ...prev,
      store_ids: prev.store_ids.includes(storeId) ? prev.store_ids.filter((id) => id !== storeId) : [...prev.store_ids, storeId],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost("CLUB_SAVE", form);
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate("/console/clubs");
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const visible = stores.filter((store) => {
    const text = `${store.store_name} ${store.city_name || ""}`.toLowerCase();
    return text.includes(query.trim().toLowerCase());
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/clubs")}>Back to clubs</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{clubId ? "Edit club" : "Create club"}</Typography>
        <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextField label="Login id" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} helperText="Used at /club to sign in as this club." />
        <TextField label={clubId ? "Password (leave blank to keep)" : "Password"} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} helperText="At least 4 characters." />
        <TextField label="Wallet amount" type="number" value={form.wallet_amount} onChange={(e) => setForm({ ...form, wallet_amount: e.target.value })} helperText="Cut when this club requests printed loyalty cards." inputProps={{ min: 0, step: "0.01" }} />
        <TextField select label="Club can change loyalty settings" value={form.club_can_edit_loyalty || "1"} onChange={(e) => setForm({ ...form, club_can_edit_loyalty: e.target.value })} helperText="If no, this club uses the console default.">
          <MenuItem value="1">Yes</MenuItem>
          <MenuItem value="0">No</MenuItem>
        </TextField>
        <TextField select label="Stores in this club can change loyalty settings" value={form.store_can_edit_loyalty || "1"} onChange={(e) => setForm({ ...form, store_can_edit_loyalty: e.target.value })} helperText="The club login can also change this later.">
          <MenuItem value="1">Yes</MenuItem>
          <MenuItem value="0">No</MenuItem>
        </TextField>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField sx={{ flex: 1 }} label="Joining code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} helperText="Shown on Create your store. After the store is approved, it joins this club." />
          <Button sx={{ textTransform: "none", mt: 1 }} onClick={() => {
            const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
            const next = Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
            setForm((prev) => ({ ...prev, code: next }));
          }}>Create code</Button>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Stores · {form.store_ids.length} selected</Typography>
          <TextField size="small" label="Search stores" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 220 }} />
        </Box>
        <Box sx={{ border: `1px solid ${consoleColors.line}`, borderRadius: 1.5 }}>
          {visible.map((store) => (
            <Box key={store.store_id} onClick={() => toggleStore(store.store_id)} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.6, cursor: "pointer", borderBottom: `1px solid ${consoleColors.line}`, "&:last-child": { borderBottom: 0 } }}>
              <Checkbox size="small" checked={form.store_ids.includes(store.store_id)} />
              <Typography sx={{ fontSize: 14 }}>{store.store_name}{store.city_name ? ` · ${store.city_name}` : ""}</Typography>
            </Box>
          ))}
          {visible.length === 0 && <Typography sx={{ p: 2, color: consoleColors.muted }}>No stores match.</Typography>}
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button onClick={() => navigate("/console/clubs")} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
